import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import SendBird, { GroupChannel } from "sendbird";
import { NewClubhouse } from "../services/clubhouseAPI";
import {
  sbCreateGroupChannelListQuery,
  sbGetPinnedGroupChannels,
  sbCreateUserMetadata,
  sbDeleteUserMetadata,
  sbGetChannelsByName,
} from "../utils/sb";

type ClubhouseState = {
  query: SendBird.GroupChannelListQuery | null;
  isLoading: boolean;
  isRefreshing: boolean;
  isFetching: boolean;
  isSearching: boolean;
  error: string | null;
  joinedClubhouses: NewClubhouse[];
  pinnedChannels: SendBird.GroupChannel[];
  selectedMembers: any[];
  newMembersChosen: any[];
};

const clubhouseSlice = createSlice({
  name: "clubhouse",
  initialState: {
    query: null,
    isLoading: true,
    isRefreshing: false,
    isFetching: false,
    isSearching: false,
    error: null,
    pinnedChannels: [],
    joinedClubhouses: [],
    selectedMembers: [],
    newMembersChosen: [],
  } as ClubhouseState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.isRefreshing = action.payload;
    },
    setFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setSearching: (state, action: PayloadAction<boolean>) => {
      state.isSearching = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setQuery: (state, action) => {
      state.query = action.payload;
    },
    setjoinedClubhouses: (
      state,
      {
        payload: { joinedClubhouses, reset = false },
      }: PayloadAction<{
        joinedClubhouses: any;
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.joinedClubhouses = joinedClubhouses;
      } else {
        const mergedChannels = [...state.joinedClubhouses, joinedClubhouses];
        state.joinedClubhouses = mergedChannels;
      }
    },
    setPinnedChannels: (
      state,
      {
        payload: { pinnedChannels },
      }: PayloadAction<{
        pinnedChannels: SendBird.GroupChannel[];
      }>
    ) => {
      state.pinnedChannels = pinnedChannels;
    },
    refresh: (state) => {
      state.isRefreshing = true;
      state.error = null;
    },
    setUnreadMessageCount: (
      state,
      {
        payload: { url, unreadMessageCount },
      }: PayloadAction<{ url: string; unreadMessageCount: number }>
    ) => {
      const updatedjoinedClubhouses = state.joinedClubhouses.map((channel) => {
        if (channel.url === url) {
          return { ...channel, unreadMessageCount };
        }

        return channel;
      });
      const updatedPinnedChannels = state.pinnedChannels.map((channel) => {
        if (channel.url === url) {
          return { ...channel, unreadMessageCount };
        }

        return channel;
      });
      state.joinedClubhouses = updatedjoinedClubhouses;
      state.pinnedChannels = updatedPinnedChannels;
    },
    replaceChannel: (
      state,
      { payload: { channel } }: PayloadAction<{ channel: any }>
    ) => {
      const updatedjoinedClubhouses = state.joinedClubhouses.map((ch) => {
        if (ch.url === channel.url) {
          return channel;
        }

        return ch;
      });

      const updatedPinnedChannels = state.pinnedChannels.map((ch) => {
        if (ch.url === channel.url) {
          return channel;
        }

        return ch;
      });

      const sortedUpdatedjoinedClubhouses = updatedjoinedClubhouses.sort(
        (a, b) => b?.lastMessage?.createdAt - a?.lastMessage?.createdAt
      );
      const sortedUpdatedPinnedChannels = updatedPinnedChannels.sort(
        (a, b) => b?.lastMessage?.createdAt - a?.lastMessage?.createdAt
      );

      state.joinedClubhouses = sortedUpdatedjoinedClubhouses;
      state.pinnedChannels = sortedUpdatedPinnedChannels;
    },
    setNotificationTriggerOption: (
      state,
      {
        payload: { url, triggerOption },
      }: PayloadAction<{ url: string; triggerOption: string }>
    ) => {
      const updatedjoinedClubhouses = state.joinedClubhouses.map((channel) => {
        if (channel.url === url) {
          return { ...channel, myPushTriggerOption: triggerOption };
        }

        return channel;
      });
      const updatedPinnedChannels = state.pinnedChannels.map((channel) => {
        if (channel.url === url) {
          return { ...channel, myPushTriggerOption: triggerOption };
        }

        return channel;
      });
      state.joinedClubhouses = updatedjoinedClubhouses;
      state.pinnedChannels = updatedPinnedChannels;
    },
    setSelectedMembers: (state, action) => {
      state.selectedMembers = action.payload;
    },
    setNewMembersChosen: (state, action) => {
      state.newMembersChosen = action.payload;
    },
  },
});

export const {
  setLoading,
  setRefreshing,
  setFetching,
  setSearching,
  setError,
  setjoinedClubhouses,
  setPinnedChannels,
  setQuery,
  refresh,
  setUnreadMessageCount,
  setNotificationTriggerOption,
  setSelectedMembers,
  setNewMembersChosen,
  replaceChannel,
} = clubhouseSlice.actions;

export const fetchPinnedChannels = () => async (dispatch) => {
  const pinnedChannels = await sbGetPinnedGroupChannels();
  const sortedPinnedChannels = pinnedChannels.sort((a: any, b: any) => {
    return b?.lastMessage?.createdAt - a?.lastMessage?.createdAt;
  });
  dispatch(setPinnedChannels({ pinnedChannels: sortedPinnedChannels }));
};

export const fetchjoinedClubhouses =
  (searchText = "") =>
  async (dispatch) => {
    dispatch(setjoinedClubhouses({ joinedClubhouses: [], reset: true }));
    dispatch(setQuery(sbCreateGroupChannelListQuery(searchText)));
  };

export const fetchNextjoinedClubhouses = () => async (dispatch, getState) => {
  try {
    const {
      clubhouse: { query },
    } = getState();
    dispatch(setFetching(true));
    const fetchedChannels = await query?.next();
    dispatch(setjoinedClubhouses({ joinedClubhouses: fetchedChannels }));
  } catch (error) {
    console.log("error", error);
  } finally {
    dispatch(setFetching(false));
    dispatch(setLoading(false));
  }
};

export const fetchChannels = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    await dispatch(fetchPinnedChannels());
    await dispatch(fetchjoinedClubhouses());
  } catch (error) {
    dispatch(setError(error?.message));
  }
};

export const refreshChannels = () => async (dispatch) => {
  dispatch(setRefreshing(true));
  dispatch(setError(null));
  try {
    await dispatch(fetchPinnedChannels());
    await dispatch(fetchjoinedClubhouses());
  } catch (error) {
    dispatch(setError(error?.message));
  } finally {
    dispatch(setRefreshing(false));
  }
};

export const handleChannelPin =
  (channelUrl, channelName) => async (dispatch, getState) => {
    const {
      clubhouse: { pinnedChannels },
    } = getState();
    try {
      if (pinnedChannels.find((ch) => ch.url === channelUrl)) {
        // un - pin
        await sbDeleteUserMetadata(channelUrl);
      } else {
        // pin

        if (pinnedChannels.length === 5) {
          // showToast({
          //   type: 'error',
          //   title: 'error',
          //   subTitle: `you have pinned 5 and reached the maximum limit. you have to remove one or more to pin ${channelName}`,
          // });
          return;
        }

        const data = {
          [channelUrl]: "true",
        };
        await sbCreateUserMetadata(data);
        // showToast({
        //   type: 'success',
        //   title: 'success',
        //   subTitle: `you have pinned 📌 ${channelName} at the top. you can pin upto 5 clubhouses`,
        // });
      }
      await dispatch(fetchPinnedChannels());
    } catch (error) {
      console.log("error", error);
    }
  };

export const fetchChannelsByName = (searchText) => async (dispatch) => {
  try {
    dispatch(setQuery(null));
    dispatch(setSearching(true));
    const channels = await sbGetChannelsByName(searchText);
    dispatch(setjoinedClubhouses({ joinedClubhouses: channels, reset: true }));
  } catch (error) {
    console.log("error", error);
  } finally {
    dispatch(setSearching(false));
  }
};

export default clubhouseSlice.reducer;
